"use client"

import React from "react"
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts"
import { format, parseISO } from "date-fns"
import { usePlatform } from "@/hooks/use-platform"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

interface TidePoint {
  time: string
  hour: number
  tideHeight: number
}

interface TideExtreme {
  time: string
  value: number
  type: "high" | "low"
}

interface TideGraphProps {
  data: TidePoint[]
  extremes?: TideExtreme[]
  unitSystem?: "metric" | "imperial"
  hoveredHour?: number | null
  onHoverChange?: (hour: number | null) => void
}

export function TideGraph({ 
  data, 
  extremes, 
  unitSystem = "metric",
  hoveredHour,
  onHoverChange
}: TideGraphProps) {
  const { isMobile } = usePlatform()

  const hasData = data && data.length > 0 && data.some(p => p.tideHeight !== undefined && p.tideHeight !== 0)

  if (!hasData) {
    return (
      <div className="h-[200px] w-full flex flex-col items-center justify-center bg-muted/20 rounded-2xl border border-dashed border-border p-6 text-center">
        <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-3">
          <span className="text-xl">🌊</span>
        </div>
        <p className="text-sm font-bold text-muted-foreground uppercase tracking-tight">No Tide Data</p>
        <p className="text-[10px] text-muted-foreground/70 uppercase tracking-widest mt-1">Predictions unavailable for this location</p>
      </div>
    )
  }

  // Convert heights based on unit system
  const chartData = data.map((point) => ({
    ...point,
    displayHeight: unitSystem === "metric" ? point.tideHeight : point.tideHeight * 3.28084,
    timeLabel: point.hour === 12 ? '12pm' : point.hour > 12 ? `${point.hour - 12}pm` : `${point.hour}am`,
  }))

  const unit = unitSystem === "metric" ? "m" : "ft"

  // Find min and max for Y axis scaling
  const heights = chartData.map(d => d.displayHeight)
  const minHeight = Math.min(...heights)
  const maxHeight = Math.max(...heights)
  const padding = (maxHeight - minHeight) * 0.2
  const yDomain = [Math.floor((minHeight - padding) * 10) / 10, Math.ceil((maxHeight + padding) * 10) / 10]

  return (
    <div className="w-full">
      <div className="h-[200px] w-full min-h-[200px] mt-4">
        <ResponsiveContainer width="100%" height="100%" minHeight={150}>
          <AreaChart 
            data={chartData} 
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            syncId="weather-charts"
            onMouseMove={(e: any) => {
              if (e && e.activePayload && e.activePayload.length) {
                const data = e.activePayload[0].payload
                if (onHoverChange && hoveredHour !== data.hour) {
                  onHoverChange(data.hour)
                }
              }
            }}
            onMouseLeave={() => {
              // Optionally clear hover state
            }}
          >
            <defs>
              <linearGradient id="colorTide" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
            <XAxis
              dataKey="hour"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#94a3b8", fontSize: 9, fontWeight: 'black' }}
              ticks={[7, 12, 17, 22]}
              tickFormatter={(h) => h === 12 ? '12pm' : h > 12 ? `${h - 12}pm` : `${h}am`}
              interval={0}
            />
            <YAxis
              hide={false}
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#94a3b8", fontSize: 9, fontWeight: 'black' }}
              domain={yDomain}
              width={40}
            />
            <RechartsTooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload
                  return (
                    <div className="bg-slate-900/95 backdrop-blur-md text-white p-3 rounded-2xl shadow-2xl border border-slate-800">
                      <p className="text-[10px] font-black uppercase tracking-widest mb-1 italic">
                        {format(parseISO(data.time), "MMM d, HH:mm")}
                      </p>
                      <p className="text-lg font-black italic">
                        {data.displayHeight.toFixed(2)}{unit}
                      </p>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                        Tide Height
                      </p>
                    </div>
                  )
                }
                return null
              }}
            />
            <Area
              type="monotone"
              dataKey="displayHeight"
              stroke="#0ea5e9"
              strokeWidth={4}
              fillOpacity={1}
              fill="url(#colorTide)"
              isAnimationActive={false}
              dot={false}
            />
            
            {/* Synced hover line */}
            {hoveredHour !== undefined && hoveredHour !== null && (
              <ReferenceLine 
                x={hoveredHour} 
                stroke="#0ea5e9" 
                strokeWidth={2} 
                strokeDasharray="3 3" 
              />
            )}

            {/* Mark extremes if available */}
            {extremes && extremes.map((extreme, idx) => {
              const displayVal = unitSystem === "metric" ? extreme.value : extreme.value * 3.28084
              const extremeHour = new Date(extreme.time).getHours()
              
              // Only show extreme if it's within the visible range (7am-10pm)
              if (extremeHour < 7 || extremeHour > 22) return null

              return (
                <ReferenceLine
                  key={idx}
                  x={extremeHour}
                  stroke={extreme.type === "high" ? "#10b981" : "#ef4444"}
                  strokeDasharray="3 3"
                  label={{
                    value: `${extreme.type === "high" ? "H" : "L"}: ${displayVal.toFixed(1)}${unit}`,
                    position: extreme.type === "high" ? "top" : "bottom",
                    fill: "#94a3b8",
                    fontSize: 8,
                    fontWeight: 'black'
                  }}
                />
              )
            })}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      
      {/* Extremes Table */}
      {extremes && extremes.length > 0 && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {extremes.slice(0, 4).map((extreme, idx) => {
            const displayVal = unitSystem === "metric" ? extreme.value : extreme.value * 3.28084
            return (
              <div key={idx} className="bg-muted/50 p-2 rounded-lg text-center">
                <p className="text-[10px] uppercase text-muted-foreground font-bold">
                  {extreme.type} Tide
                </p>
                <p className="text-sm font-bold">
                  {format(parseISO(extreme.time), "HH:mm")}
                </p>
                <p className="text-xs text-primary">
                  {displayVal.toFixed(1)} {unit}
                </p>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
